import type { OwnerKind } from "@coach/shared";
import type { LocalDb } from "@coach/sync";
import { enqueueOp, newId, nowIso, selectAll, withLocalWrite } from "./base.js";

export const EVENT_TYPES = ["training", "match", "meeting", "medical", "travel", "other"] as const;
export type EventType = (typeof EVENT_TYPES)[number];

/**
 * Calendar events are polymorphic by owner. Default in v2 is
 * `ownerKind="coach"` with `ownerId=<userId>` (personal calendar). Team
 * events use `ownerKind="team"` with `ownerId=<teamId>` and surface to
 * every coach assigned to the team.
 */
export interface EventRow {
  id: string;
  org_id: string;
  rev: number | null;
  updated_at: string;
  deleted_at: string | null;
  client_id: string | null;
  owner_kind: string;
  owner_id: string;
  title: string;
  description: string | null;
  type: string;
  starts_at: string;
  ends_at: string | null;
  location: string | null;
  /** Set when the event belongs to a recurrence series. */
  series_id: string | null;
  /** SQLite stores booleans as 0/1. */
  all_day: number;
  // Satisfy LocalDbRow's string index signature without losing field types.
  [key: string]: string | number | null;
}

export async function listEventsInRange(
  db: LocalDb,
  orgId: string,
  ownerId: string,
  fromIso: string,
  toIso: string,
  ownerKind: OwnerKind = "coach",
): Promise<EventRow[]> {
  return selectAll<EventRow>(
    db,
    `SELECT * FROM calendar_event
       WHERE org_id = ? AND owner_kind = ? AND owner_id = ? AND deleted_at IS NULL
         AND starts_at >= ? AND starts_at < ?
       ORDER BY starts_at ASC`,
    [orgId, ownerKind, ownerId, fromIso, toIso],
  );
}

export async function listUpcomingEvents(
  db: LocalDb,
  orgId: string,
  ownerId: string,
  fromIso: string,
  limit = 5,
  ownerKind: OwnerKind = "coach",
): Promise<EventRow[]> {
  return selectAll<EventRow>(
    db,
    `SELECT * FROM calendar_event
       WHERE org_id = ? AND owner_kind = ? AND owner_id = ? AND deleted_at IS NULL
         AND starts_at >= ?
       ORDER BY starts_at ASC
       LIMIT ?`,
    [orgId, ownerKind, ownerId, fromIso, limit],
  );
}

export interface UpsertEventInput {
  id?: string;
  orgId: string;
  ownerId: string;
  ownerKind?: OwnerKind;
  title: string;
  description: string | null;
  type: EventType;
  startsAt: string;
  endsAt: string | null;
  location: string | null;
  seriesId?: string | null;
  allDay?: boolean;
}

/** Insert-or-update; if `id` is omitted a new row is created. */
export async function upsertEvent(db: LocalDb, input: UpsertEventInput): Promise<EventRow> {
  const id = input.id ?? newId();
  const updatedAt = nowIso();
  const seriesId = input.seriesId ?? null;
  const allDay = input.allDay ? 1 : 0;
  const ownerKind = input.ownerKind ?? "coach";

  const row: EventRow = {
    id,
    org_id: input.orgId,
    rev: null,
    updated_at: updatedAt,
    deleted_at: null,
    client_id: null,
    owner_kind: ownerKind,
    owner_id: input.ownerId,
    title: input.title,
    description: input.description,
    type: input.type,
    starts_at: input.startsAt,
    ends_at: input.endsAt,
    location: input.location,
    series_id: seriesId,
    all_day: allDay,
  };

  await withLocalWrite(db, async (tx) => {
    const existing = (
      await tx.query<EventRow>(`SELECT rev FROM calendar_event WHERE id = ? LIMIT 1`, [id])
    )[0];

    if (existing) {
      await tx.exec(
        `UPDATE calendar_event
           SET title = ?, description = ?, type = ?, starts_at = ?, ends_at = ?,
               location = ?, series_id = ?, all_day = ?, updated_at = ?, deleted_at = NULL
           WHERE id = ?`,
        [
          input.title,
          input.description,
          input.type,
          input.startsAt,
          input.endsAt,
          input.location,
          seriesId,
          allDay,
          updatedAt,
          id,
        ],
      );
    } else {
      await tx.exec(
        `INSERT INTO calendar_event
           (id, org_id, rev, updated_at, deleted_at, client_id, owner_kind, owner_id, title,
            description, type, starts_at, ends_at, location, series_id, all_day)
           VALUES (?, ?, NULL, ?, NULL, NULL, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          id,
          input.orgId,
          updatedAt,
          ownerKind,
          input.ownerId,
          input.title,
          input.description,
          input.type,
          input.startsAt,
          input.endsAt,
          input.location,
          seriesId,
          allDay,
        ],
      );
    }

    await enqueueOp(tx, {
      entity: "calendar_event",
      kind: "upsert",
      payload: {
        id,
        orgId: input.orgId,
        ownerKind,
        ownerId: input.ownerId,
        title: input.title,
        description: input.description,
        type: input.type,
        startsAt: input.startsAt,
        endsAt: input.endsAt,
        location: input.location,
        seriesId,
        allDay: Boolean(allDay),
      },
      updatedAt,
      baseRev: existing?.rev ?? null,
    });
  });

  return row;
}

export async function deleteEvent(db: LocalDb, args: { id: string; orgId: string }): Promise<void> {
  const updatedAt = nowIso();
  await withLocalWrite(db, async (tx) => {
    await tx.exec(
      `UPDATE calendar_event SET deleted_at = ?, updated_at = ?
         WHERE id = ? AND org_id = ?`,
      [updatedAt, updatedAt, args.id, args.orgId],
    );
    await enqueueOp(tx, {
      entity: "calendar_event",
      kind: "delete",
      payload: { id: args.id },
      updatedAt,
      baseRev: null,
    });
  });
}

/**
 * Bulk-delete every event in a series whose `starts_at` is at or after the
 * pivot timestamp. When `fromIso` is `null` the entire series is removed.
 * Each affected row is enqueued individually so the sync engine can push
 * deletes one-by-one to the server.
 */
export async function deleteSeries(
  db: LocalDb,
  args: { seriesId: string; orgId: string; fromIso: string | null },
): Promise<number> {
  const targets = await selectAll<{ id: string }>(
    db,
    args.fromIso
      ? `SELECT id FROM calendar_event
           WHERE series_id = ? AND org_id = ? AND deleted_at IS NULL
             AND starts_at >= ?`
      : `SELECT id FROM calendar_event
           WHERE series_id = ? AND org_id = ? AND deleted_at IS NULL`,
    args.fromIso ? [args.seriesId, args.orgId, args.fromIso] : [args.seriesId, args.orgId],
  );
  for (const t of targets) {
    await deleteEvent(db, { id: t.id, orgId: args.orgId });
  }
  return targets.length;
}

/**
 * Bulk-update every event in a series whose `starts_at` is at or after the
 * pivot timestamp. Preserves each instance's own start/end day — only the
 * fields supplied in `changes` are applied (title, description, type,
 * location, allDay, plus optional time-of-day shifts).
 */
export async function updateSeries(
  db: LocalDb,
  args: {
    seriesId: string;
    orgId: string;
    fromIso: string | null;
    changes: {
      title?: string;
      description?: string | null;
      type?: EventType;
      location?: string | null;
      allDay?: boolean;
      startTime?: string | null;
      endTime?: string | null;
    };
  },
): Promise<number> {
  const rows = await selectAll<EventRow>(
    db,
    args.fromIso
      ? `SELECT * FROM calendar_event
           WHERE series_id = ? AND org_id = ? AND deleted_at IS NULL
             AND starts_at >= ?`
      : `SELECT * FROM calendar_event
           WHERE series_id = ? AND org_id = ? AND deleted_at IS NULL`,
    args.fromIso ? [args.seriesId, args.orgId, args.fromIso] : [args.seriesId, args.orgId],
  );

  for (const row of rows) {
    const startDate = new Date(row.starts_at);
    const endDate = row.ends_at ? new Date(row.ends_at) : null;
    if (args.changes.startTime) {
      const [hh, mm] = args.changes.startTime.split(":").map(Number);
      startDate.setHours(hh ?? 0, mm ?? 0, 0, 0);
    }
    if (args.changes.endTime) {
      const target = endDate ?? new Date(startDate);
      const [hh, mm] = args.changes.endTime.split(":").map(Number);
      target.setHours(hh ?? 0, mm ?? 0, 0, 0);
      if (target.getTime() <= startDate.getTime()) {
        target.setDate(target.getDate() + 1);
      }
      await upsertEvent(db, {
        id: row.id,
        orgId: row.org_id,
        ownerKind: row.owner_kind as OwnerKind,
        ownerId: row.owner_id,
        title: args.changes.title ?? row.title,
        description:
          args.changes.description !== undefined ? args.changes.description : row.description,
        type: (args.changes.type ?? (row.type as EventType)) as EventType,
        startsAt: startDate.toISOString(),
        endsAt: target.toISOString(),
        location: args.changes.location !== undefined ? args.changes.location : row.location,
        seriesId: row.series_id,
        allDay: args.changes.allDay !== undefined ? args.changes.allDay : Boolean(row.all_day),
      });
      continue;
    }
    await upsertEvent(db, {
      id: row.id,
      orgId: row.org_id,
      ownerKind: row.owner_kind as OwnerKind,
      ownerId: row.owner_id,
      title: args.changes.title ?? row.title,
      description:
        args.changes.description !== undefined ? args.changes.description : row.description,
      type: (args.changes.type ?? (row.type as EventType)) as EventType,
      startsAt: startDate.toISOString(),
      endsAt: endDate ? endDate.toISOString() : null,
      location: args.changes.location !== undefined ? args.changes.location : row.location,
      seriesId: row.series_id,
      allDay: args.changes.allDay !== undefined ? args.changes.allDay : Boolean(row.all_day),
    });
  }

  return rows.length;
}
